import Button, { NavigableButton } from '@carvana/ds-button';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import React, { useRef } from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  INACTIVE_SLIDE_OPACITY,
  SlideContentWrapper
} from './MarketingCarousel.styles';

type SlideContentProps = {
  className?: string;
  heading: string;
  subHeading: string;
};

function SlideContent(props: SlideContentProps) {
  return (
    <div className={cx(props.className, 'flex flex-col gap-y-lg')}>
      <h2 className="t-heading-md sm:t-heading-xl">{props.heading}</h2>
      <div className="grid t-body-sm">
        <p className="col-span-full row-span-full">{props.subHeading}</p>
      </div>
    </div>
  );
}

type OwnProps = {
  backgroundColor: string;
  cta: {
    action: string | ((ev: React.MouseEvent) => void);
    nativePayload?: string;
    text: string;
  };
  heading: string;
  image: {
    alt: string;
    desktop: string;
    mobile: string;
    tablet: string;
  };
  isActive?: boolean;
  opacity: number;
  spacerHeading: string;
  spacerSubHeading: string;
  mobileImageHeight?: string;
  subHeading: string;
};

export type { OwnProps as SlideProps };

export default function Slide(props: OwnProps) {
  const isMobile = useIsMobile();
  const dragStartRef = useRef(0);
  const hasMobileImageHeight = props.mobileImageHeight && isMobile;

  return (
    <div
      className={cx(
        'grid cursor-grab overflow-hidden rounded-xl duration-[400ms] active:cursor-grabbing sm:transition-opacity',
        'bg-[var(--background-color)]'
      )}
      style={
        {
          '--background-color': props.backgroundColor,
          opacity: props.opacity
        } as React.CSSProperties
      }
    >
      <picture className="pointer-events-none flex w-full md:col-span-full md:row-span-full">
        <source
          media={`(min-width: ${breakpoints.md}px)`}
          srcSet={props.image.desktop}
        />
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={props.image.tablet}
        />
        <img
          src={props.image.mobile}
          alt={props.image.alt}
          className={cx(
            'w-full',
            hasMobileImageHeight && `${props.mobileImageHeight} object-cover`
          )}
          loading={props.isActive ? 'eager' : 'lazy'}
        />
      </picture>

      <SlideContentWrapper
        style={{
          // Normalize opacity from [INACTIVE_SLIDE_OPACITY, 1] to [0, 1]
          // so inactive slide content fades to transparent while the slide
          // container itself remains at INACTIVE_SLIDE_OPACITY
          opacity: Math.max(
            0,
            (props.opacity - INACTIVE_SLIDE_OPACITY) /
              (1 - INACTIVE_SLIDE_OPACITY)
          )
        }}
      >
        <div className="grid">
          <SlideContent
            className="col-span-full row-span-full justify-end"
            heading={props.heading}
            subHeading={props.subHeading}
          />
          {/* Render spacer content so all slides are the same height */}
          {!hasMobileImageHeight && (
            <SlideContent
              className={cx(
                'col-span-full row-span-full',
                'user-select-none pointer-events-none invisible'
              )}
              heading={props.spacerHeading}
              subHeading={props.spacerSubHeading}
            />
          )}
        </div>

        {typeof props.cta.action === 'string' ? (
          <NavigableButton
            className="sm:w-fit"
            data-analytics-attributes={JSON.stringify({
              text: props.cta.text
            })}
            data-analytics-id="Carousel Slide"
            data-native-payload={props.cta.nativePayload}
            data-testid="marketing-slide-link"
            draggable={false}
            href={props.cta.action}
            size={isMobile ? 'sm' : 'md'}
            variant="secondaryInverted"
          >
            {props.cta.text}
          </NavigableButton>
        ) : (
          <Button
            className="sm:w-fit"
            data-analytics-attributes={JSON.stringify({
              text: props.cta.text
            })}
            data-analytics-id="Carousel Slide"
            data-testid="marketing-slide-button"
            draggable={false}
            onClick={ev => {
              if (
                typeof props.cta.action === 'function' &&
                Math.abs(ev.clientX - dragStartRef.current) < 5
              ) {
                props.cta.action(ev);
              }
            }}
            onMouseDown={ev => {
              dragStartRef.current = ev.clientX;
            }}
            onTouchStart={ev => {
              dragStartRef.current = ev.touches[0].clientX;
            }}
            size={isMobile ? 'sm' : 'md'}
            variant="secondaryInverted"
          >
            {props.cta.text}
          </Button>
        )}
      </SlideContentWrapper>
    </div>
  );
}
